import queryString from 'query-string';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getAcademicYearType } from '~utils/use-academic-year';
import { useLocalStorage } from './use-local-storage';
import { useViewer } from './use-viewer';

const currentViewPropertySessionContext = createContext([
  {
    currentViewProperty: null,
    currentViewAcademicYear: null,
    currentViewLandlordAcademicYear: null,
    currentViewAcademicYearType: null,
    currentViewLandLord: null,
  },
  { onPropertyChange: null, onAcademicYearChange: null, onLandLordChange: null },
]);

const propertyIdIsValid = (propertyId, properties) =>
  properties.some(({ id }) => id === propertyId);

const landlordIdIsValid = (landlordId, landlords) =>
  (landlords || []).some(({ id }) => id === landlordId);

const academicYearIdIsValid = (academicYearId, academicYears) =>
  academicYears.some(({ id }) => id === academicYearId);

export const CurrentViewPropertySessionProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const { currentRole } = useViewer();
  const properties = currentRole?.accessScope.properties;
  const landlords = currentRole?.accessScope.landlords;
  const [currentViewPropertyIdFromStorage, setStorageCurrentViewPropertyId] = useLocalStorage(
    'currentViewPropertyId'
  );
  const [
    currentViewAcademicYearIdFromStorage,
    setStorageCurrentViewAcademicYearId,
  ] = useLocalStorage('currentViewAcademicYearId');

  const [
    currentViewLandlordAcademicYearIdFromStorage,
    setStorageCurrentViewLandlordAcademicYearId,
  ] = useLocalStorage('currentViewLandlordAcademicYearId');

  const [currentViewLandLordIdFromStorage, setStorageCurrentViewLandLordId] = useLocalStorage(
    'currentViewLandLordId'
  );

  // ID priorities:
  // Url search > storage > default
  const defaultViewPropertyId = useMemo(() => {
    const propertyIdFromUrlSearch = queryString.parse(location.search).propertyId;

    if (propertyIdIsValid(propertyIdFromUrlSearch, properties)) {
      setStorageCurrentViewPropertyId(propertyIdFromUrlSearch);
      return propertyIdFromUrlSearch;
    } else if (propertyIdIsValid(currentViewPropertyIdFromStorage, properties)) {
      return currentViewPropertyIdFromStorage;
    } else {
      const propertyId = properties[0]?.id;
      setStorageCurrentViewPropertyId(propertyId);
      return propertyId;
    }
  }, [
    currentViewPropertyIdFromStorage,
    location.search,
    properties,
    setStorageCurrentViewPropertyId,
  ]);

  const defaultViewLandLordId = useMemo(() => {
    if (landlordIdIsValid(currentViewLandLordIdFromStorage, landlords)) {
      return currentViewLandLordIdFromStorage;
    } else {
      const landlordId = landlords?.[0]?.id;
      setStorageCurrentViewLandLordId(landlordId);
      return landlordId;
    }
  }, [currentViewLandLordIdFromStorage, landlords, setStorageCurrentViewLandLordId]);

  const defaultViewAcademicYearId = useMemo(() => {
    if (!defaultViewPropertyId) return '';

    const academicYearIdFromUrlSearch = queryString.parse(location.search).academicYearId;
    const defaultViewProperty = properties.find(({ id }) => id === defaultViewPropertyId);

    const defaultViewPropertyAcademicYears = [
      ...defaultViewProperty.academicYear.past,
      ...defaultViewProperty.academicYear.current,
    ];

    if (academicYearIdIsValid(academicYearIdFromUrlSearch, defaultViewPropertyAcademicYears)) {
      setStorageCurrentViewAcademicYearId(academicYearIdFromUrlSearch);
      return academicYearIdFromUrlSearch;
    } else if (
      academicYearIdIsValid(currentViewAcademicYearIdFromStorage, defaultViewPropertyAcademicYears)
    ) {
      return currentViewAcademicYearIdFromStorage;
    } else if (defaultViewProperty.academicYear.current?.length === 0) {
      const academicYearId = defaultViewProperty.academicYear.past[0]?.id;
      return academicYearId;
    } else {
      const academicYearId = defaultViewProperty.academicYear.current[0]?.id;
      setStorageCurrentViewAcademicYearId(academicYearId);
      return academicYearId;
    }
  }, [
    currentViewAcademicYearIdFromStorage,
    defaultViewPropertyId,
    location.search,
    properties,
    setStorageCurrentViewAcademicYearId,
  ]);

  const defaultViewLandlordAcademicYearId = useMemo(() => {
    if (!defaultViewLandLordId) return '';
    const defaultViewLandLord = landlords.find(({ id }) => id === defaultViewLandLordId);
    const defaultViewPropertyAcademicYears = defaultViewLandLord?.groupAcademicYears || [];
    if (
      defaultViewPropertyAcademicYears.find(
        (item) => item?.id === currentViewLandlordAcademicYearIdFromStorage
      )
    ) {
      return currentViewLandlordAcademicYearIdFromStorage;
    } else {
      const academicYearId = defaultViewPropertyAcademicYears[0]?.id;
      setStorageCurrentViewLandlordAcademicYearId(academicYearId);
      return academicYearId;
    }
  }, [
    currentViewLandlordAcademicYearIdFromStorage,
    defaultViewLandLordId,
    landlords,
    setStorageCurrentViewLandlordAcademicYearId,
  ]);

  const [currentViewLandLordId, setCurrentViewLandLordId] = useState(defaultViewLandLordId);

  const [currentViewPropertyId, setCurrentViewPropertyId] = useState(defaultViewPropertyId);
  const [currentViewAcademicYearId, setCurrentViewAcademicYearId] = useState(
    defaultViewAcademicYearId
  );

  const [currentViewLandlordAcademicYearId, setCurrentViewLandlordAcademicYearId] = useState(
    defaultViewLandlordAcademicYearId
  );

  const currentViewProperty = useMemo(
    () =>
      currentViewPropertyId ? properties.find(({ id }) => id === currentViewPropertyId) : null,
    [currentViewPropertyId, properties]
  );

  const currentViewLandLord = useMemo(
    () => (currentViewLandLordId ? landlords.find(({ id }) => id === currentViewLandLordId) : null),
    [currentViewLandLordId, landlords]
  );

  useEffect(() => {
    if (currentViewPropertyId) {
      window.localStorage.setItem('currentViewPropertyId', currentViewPropertyId);
    }
  }, [currentViewPropertyId]);

  const currentViewAcademicYear = useMemo(() => {
    if (!currentViewProperty) return null;

    const currentViewPropertyAcademicYears = [
      ...currentViewProperty.academicYear.past,
      ...currentViewProperty.academicYear.current,
    ];

    if (
      currentViewProperty.academicYear.current?.length === 0 &&
      currentViewPropertyAcademicYears?.length > 0
    ) {
      return currentViewPropertyAcademicYears[0];
    }

    return currentViewPropertyAcademicYears.find(({ id }) => id === currentViewAcademicYearId);
  }, [currentViewAcademicYearId, currentViewProperty]);

  const currentViewLandlordAcademicYear = useMemo(() => {
    if (!currentViewLandLord) return null;
    const currentViewPropertyAcademicYears = currentViewLandLord?.groupAcademicYears || [];
    return currentViewPropertyAcademicYears.find(
      ({ id }) => id === currentViewLandlordAcademicYearId
    );
  }, [currentViewLandLord, currentViewLandlordAcademicYearId]);

  const currentViewAcademicYearType = useMemo(
    () =>
      currentViewProperty && currentViewAcademicYear
        ? getAcademicYearType(currentViewProperty.academicYear, currentViewAcademicYear.id)
        : null,
    [currentViewAcademicYear, currentViewProperty]
  );

  const replaceUrlSearchWithViewPropertySession = useCallback(
    ({ propertyId, academicYearId } = {}) => {
      const search = queryString.parse(location.search);
      // If url search has propertyId or academicYearId,
      // replace them with the new ones
      // else just set the state without changing url search
      if (search.propertyId || search.academicYearId) {
        history.push(
          `?${queryString.stringify({
            ...search,
            propertyId: search.propertyId ? propertyId : undefined,
            academicYearId: search.academicYearId ? academicYearId : undefined,
          })}${location.hash}`
        );
      }
    },
    [history, location.hash, location.search]
  );

  const onPropertyChange = useCallback(
    (propertyId) => {
      const property = properties.find(({ id }) => id === propertyId);

      if (properties.some(({ id }) => id === propertyId) && propertyId !== currentViewProperty.id) {
        const academicYearId = property.academicYear.current[0]?.id ?? null;

        setCurrentViewPropertyId(propertyId);
        setCurrentViewAcademicYearId(academicYearId);
        setStorageCurrentViewPropertyId(propertyId);
        setStorageCurrentViewAcademicYearId(academicYearId);
        replaceUrlSearchWithViewPropertySession({ propertyId, academicYearId });
      }
    },
    [
      currentViewProperty.id,
      properties,
      replaceUrlSearchWithViewPropertySession,
      setStorageCurrentViewAcademicYearId,
      setStorageCurrentViewPropertyId,
    ]
  );

  const onLandLordChange = useCallback(
    (landlordId) => {
      if (
        (landlords || []).some(({ id }) => id === landlordId) &&
        landlordId !== currentViewLandLord?.id
      ) {
        const findLandlords = (landlords || []).find((item) => item?.id === landlordId);
        const academicYearId = findLandlords?.groupAcademicYears?.[0]?.id ?? null;
        setStorageCurrentViewLandLordId(landlordId);
        setCurrentViewLandlordAcademicYearId(academicYearId);
        setStorageCurrentViewLandlordAcademicYearId(academicYearId);
        setCurrentViewLandLordId(landlordId);
      }
    },
    [
      currentViewLandLord?.id,
      landlords,
      setStorageCurrentViewLandLordId,
      setStorageCurrentViewLandlordAcademicYearId,
    ]
  );

  const onAcademicYearChange = useCallback(
    (academicYearId, showLandlordSelection) => {
      const currentViewPropertyAcademicYears = [
        ...currentViewProperty.academicYear.current,
        ...currentViewProperty.academicYear.past,
      ];

      if (!showLandlordSelection) {
        if (
          currentViewPropertyAcademicYears.some(({ id }) => id === academicYearId) &&
          academicYearId !== currentViewAcademicYear?.id
        ) {
          const isActiveAcademicYear = currentViewPropertyAcademicYears.find(
            (item) => item.id === academicYearId && item.isCurrent
          );
          if (isActiveAcademicYear) {
            setStorageCurrentViewAcademicYearId(academicYearId);
          }

          setCurrentViewAcademicYearId(academicYearId);
          replaceUrlSearchWithViewPropertySession({
            propertyId: currentViewPropertyId,
            academicYearId,
          });
        }
      } else {
        if (
          (landlords.find(({ id }) => id === defaultViewLandLordId)?.groupAcademicYears || []).some(
            ({ id }) => id === academicYearId
          ) &&
          academicYearId !== currentViewLandlordAcademicYear?.id
        ) {
          setStorageCurrentViewLandlordAcademicYearId(academicYearId);
          setCurrentViewLandlordAcademicYearId(academicYearId);
        }
      }
    },
    [
      currentViewProperty.academicYear,
      currentViewAcademicYear?.id,
      replaceUrlSearchWithViewPropertySession,
      currentViewPropertyId,
      setStorageCurrentViewAcademicYearId,
      landlords,
      currentViewLandlordAcademicYear?.id,
      defaultViewLandLordId,
      setStorageCurrentViewLandlordAcademicYearId,
    ]
  );

  const onRoutingChange = () => {
    setCurrentViewAcademicYearId(defaultViewAcademicYearId);
  };

  return (
    <currentViewPropertySessionContext.Provider
      value={[
        {
          currentViewProperty,
          currentViewAcademicYear,
          currentViewLandlordAcademicYear,
          currentViewAcademicYearType,
          currentViewLandLord,
        },
        { onPropertyChange, onAcademicYearChange, onRoutingChange, onLandLordChange },
      ]}
    >
      {children}
    </currentViewPropertySessionContext.Provider>
  );
};

export const useCurrentViewPropertySession = () => useContext(currentViewPropertySessionContext);
